import React from 'react';
import backgroundImg from '../../../assets/images/appointment_screen/bg-image.jpg';
import momAndBaby from '../../../assets/images/appointment_screen/momAndBaby.jpg';
import girlSmiling from '../../../assets/images/appointment_screen/girl_smiling.png';
import { motion } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';

const DuringEvaluationSection = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 955px)' });

  const TitleAndDescription = ({ title, description }) => {
    return (
      <div className="flex flex-col gap-1">
        <h2 className="text-[26px] sm:text-[28px] text-white font-bold">
          {title}
        </h2>
        <p
          className="text-lg text-white font-light max-w-auto lg:max-w-[400px]"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>
    );
  };

  return (
    <div
      className="w-full bg-cover bg-center bg-no-repeat flex"
      style={{
        backgroundImage: `url(${backgroundImg})`,
      }}
    >
      <main className="relative max-w-[1200px] mx-auto px-4 md:px-10 py-10 md:py-20 z-10">
        <div className="mb-8">
          <h5 className="text-[26px] text-white">What to expect... </h5>
          <h2 className="text-[34px] sm:text-[40px] md:text-[45px] text-white font-bold leading-[50px]">
            During the evaluation?
          </h2>
        </div>
        <div className="flex flex-col gap-10">
          <div className="flex flex-col md:flex-row gap-10 md:gap-20">
            <div className="flex-1 flex flex-col gap-4 sm:gap-10">
              <TitleAndDescription
                title="Arrive 5-10 Minutes Early"
                description="Please arrive 5-10 minutes early to your child’s evaluation, or earlier if you have not yet completed the intake paperwork. You will be greeted by our friendly office staff, who will invite you to relax in our waiting room while your child’s therapist prepares for the evaluation."
              />
              <TitleAndDescription
                title="Parents Required at Appointments"
                description="Parents are generally requested to be present during the entire evaluation. Your child’s therapist will ask questions regarding your child’s gross and fine motor and/or language development, medical and developmental history, social life, school, hobbies, home set-up, current struggles, and parent goals. Your therapist will complete one or more standardized assessments to get a sense of your child’s baseline function and determine specific impairments as well as the need for therapy interventions. Once the evaluation is complete, your therapist will provide recommendations for further treatment and frequency for follow-up visits."
              />
            </div>
            <div className="flex-1 flex flex-col gap-4 sm:gap-10">
              <TitleAndDescription
                title="Appointment length"
                description="Evaluations are typically 60 minutes in length. For dyslexia evaluations (typically completed via telehealth), evaluations may be scheduled for 120 minutes, or two separate 60-minute sessions, to allow more time for the extensive assessments required. "
              />
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.5 }}
              >
                <img
                  src={momAndBaby}
                  alt="Physical therapist assisting child in Wasilla clinic"
                  className="rounded-3xl max-w-auto sm:max-w-[400px]"
                />
              </motion.div>
            </div>
          </div>
        </div>
        <div className="mb-8 mt-14">
          <h5 className="text-[26px] text-white">What to expect... </h5>
          <h2 className="text-[34px] sm:text-[40px] md:text-[45px] text-white font-bold leading-[50px]">
            After your evaluation?
          </h2>
        </div>
        <div className="flex flex-col gap-10">
          <div className="flex flex-col md:flex-row gap-10 md:gap-20">
            <div className="flex-1 flex flex-col gap-4 sm:gap-10">
              <TitleAndDescription
                title="Follow-up Visit"
                description="After your evaluation, our receptionist will schedule your child for some follow-up appointments, based on the therapist’s recommended visit frequency. Follow-up visits will typically last 55-60 minutes in length. Your child’s therapist may request you to sit in during your child’s future appointments to observe treatment, or you may be able to wait in our lobby during sessions if your child focuses better without parental observation."
              />
              <motion.div
                initial={{ opacity: 0, x: isMobile ? 100 : 300 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.5 }}
              >
                <img
                  src={girlSmiling}
                  alt="Child in Wasilla pediatric therapy with therapy ball"
                  className="rounded-3xl w-[90vw] block md:hidden"
                />
              </motion.div>
            </div>
            <div className="flex-1 flex flex-col gap-4 sm:gap-10">
              <TitleAndDescription
                title="Home Exercise Recommendations"
                description={`
                At your first follow-up appointment, your child’s therapist will provide a printout of the initial evaluation paperwork, standardized test scores and interpretation, home exercise program and recommendations, and any other educational resources. You will have the opportunity to discuss your child’s therapy plan of care and ask questions related to treatment and therapy goals. Your child’s evaluation paperwork and progress notes (completed every 60 days following evaluation) will be shared with the referring provider to keep them updated on your child’s progress in therapy.
                <br/>
                <br/>
                Follow-up appointments may be conducted in-person in our clinic, via telehealth, or a combination of both. The decision for the method of follow-up visits will depend on your child’s treatment plan, your family’s unique situation, and the therapist’s recommendations. Please reach out to your child’s therapist directly with any questions or concerns regarding follow-up appointments.
                `}
              />
              <TitleAndDescription
                title="Cancellation Policy"
                description="If you need to cancel any therapy appointments, please reach out to us with <span class='font-semibold'>more than 24 hours of notice to avoid late cancellations fees</span> and to allow us to reschedule another patient into that slot."
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-10 lg:-left-[60px]">
          <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : isTablet ? -100 : -300 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.5 }}
          >
            <img
              src={girlSmiling}
              alt="Child in Wasilla pediatric therapy with therapy ball"
              className="rounded-3xl max-h-[440px] lg:max-h-[540px] hidden md:block"
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default DuringEvaluationSection;
